import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

export default function TransitVisaAdminList() {
  const navigate = useNavigate();

  const [rows, setRows]     = useState([]);
  const [meta, setMeta]     = useState({ total: 0, page: 1, limit: 10, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [err, setErr]       = useState("");

  const [search, setSearch]           = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [page, setPage]               = useState(1);
  const [limit, setLimit]             = useState(10);

  const fetchList = async (opts = {}) => {
    const nextPage  = opts.page  ?? page;
    const nextLimit = opts.limit ?? limit;

    setLoading(true);
    setErr("");
    try {
      const res = await api.get("/admin/transit-visa-requirements", {
        params: {
          search:       search       || undefined,
          country_code: countryCode  || undefined,
          page:         nextPage,
          limit:        nextLimit,
        },
      });
      setRows(res.data?.data || []);
      setMeta(res.data?.meta || { total: 0, page: nextPage, limit: nextLimit, totalPages: 1 });
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to load data";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList({ page: 1 });
  }, []);

  const onApply = (e) => {
    e.preventDefault();
    setPage(1);
    fetchList({ page: 1 });
  };

  const onReset = () => {
    setSearch("");
    setCountryCode("");
    setPage(1);
    // Use fresh values directly
    setTimeout(() => fetchList({ page: 1 }), 0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this transit visa requirement?")) return;
    try {
      await api.delete(`/admin/transit-visa-requirements/${id}`);
      toast.success("Deleted successfully");
      fetchList({ page });
    } catch (e) {
      toast.error(e?.response?.data?.message || "Delete failed");
    }
  };

  const goPrev = () => {
    if (meta.page <= 1) return;
    const p = meta.page - 1;
    setPage(p);
    fetchList({ page: p });
  };

  const goNext = () => {
    if (meta.page >= meta.totalPages) return;
    const p = meta.page + 1;
    setPage(p);
    fetchList({ page: p });
  };

  const onLimitChange = (e) => {
    const v = Number(e.target.value) || 10;
    setLimit(v);
    setPage(1);
    fetchList({ page: 1, limit: v });
  };

  const renderBadges = (items = []) => {
    if (!items || items.length === 0)
      return <span className="text-gray-400 text-xs">—</span>;
    return (
      <div className="flex flex-wrap gap-1">
        {items.map((item, i) => (
          <span
            key={i}
            className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 border border-blue-100"
          >
            {item}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Transit Visa Requirements</h1>
        <button
          className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90 self-start"
          onClick={() => navigate("/admin/transit-visa-requirements/create")}
        >
          + Add Requirement
        </button>
      </div>

      {/* Filters */}
      <form
        onSubmit={onApply}
        className="mt-6 flex flex-wrap items-end gap-3 border rounded-2xl p-4"
      >
        <div className="min-w-[240px]">
          <label className="text-sm font-semibold">Search</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            placeholder="Country, notes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="min-w-[180px]">
          <label className="text-sm font-semibold">Country Code</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black uppercase"
            placeholder="e.g. GB"
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value.toUpperCase())}
          />
        </div>

        <div className="min-w-[140px]">
          <label className="text-sm font-semibold">Per page</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={limit}
            onChange={onLimitChange}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>

        <button
          type="submit"
          className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90"
        >
          Apply
        </button>

        <button
          type="button"
          className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
          onClick={onReset}
        >
          Reset
        </button>
      </form>

      {err && <div className="mt-4 text-sm text-red-600">{err}</div>}

      {/* Table */}
      <div className="mt-6 border rounded-2xl overflow-hidden">
        {/* Table header row with pagination controls */}
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <span>
            Transit Visa List{" "}
            <span className="text-gray-500 font-normal">({meta.total} records)</span>
          </span>

          <div className="flex flex-wrap items-center gap-2 text-sm">
            <button
              type="button"
              onClick={goPrev}
              disabled={meta.page <= 1 || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
            >
              Prev
            </button>

            <span className="text-gray-700">
              Page <b>{meta.page}</b> / <b>{meta.totalPages}</b> (Total: <b>{meta.total}</b>)
            </span>

            <button
              type="button"
              onClick={goNext}
              disabled={meta.page >= meta.totalPages || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-6 text-sm text-gray-500">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No records found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-t bg-white">
                <tr className="text-left">
                  <th className="px-4 py-3 whitespace-nowrap">Layover Country</th>
                  <th className="px-4 py-3 whitespace-nowrap">Layover Code</th>
                  <th className="px-4 py-3 whitespace-nowrap">When Required</th>
                  <th className="px-4 py-3 whitespace-nowrap">Exempt If Holding Visa From</th>
                  <th className="px-4 py-3 whitespace-nowrap">Exemption Codes</th>
                  <th className="px-4 py-3 whitespace-nowrap">Notes</th>
                  <th className="px-4 py-3 whitespace-nowrap">Actions</th>
                </tr>
              </thead>

              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-t hover:bg-gray-50 align-top">
                    <td className="px-4 py-3 font-semibold whitespace-nowrap">
                      {row.country}
                    </td>

                    <td className="px-4 py-3">
                      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-bold">
                        {row.country_code}
                      </span>
                    </td>

                    <td className="px-4 py-3 max-w-[200px] text-gray-600 text-xs leading-relaxed">
                      {row.when_required || <span className="text-gray-400">—</span>}
                    </td>

                    <td className="px-4 py-3 max-w-[220px]">
                      {renderBadges(row.exemption_countries)}
                    </td>

                    <td className="px-4 py-3 max-w-[180px]">
                      {renderBadges(row.exemption_country_codes)}
                    </td>

                    <td className="px-4 py-3 max-w-[200px] text-gray-600 text-xs leading-relaxed">
                      {row.notes || <span className="text-gray-400">—</span>}
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50"
                          onClick={() =>
                            navigate(`/admin/transit-visa-requirements/edit/${row.id}`)
                          }
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="rounded-lg border border-red-200 px-3 py-1 text-xs font-semibold text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(row.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}